import { useCallback, useMemo } from 'react'
import { Schemas } from '~/api/gen'
import { Img } from '~/components/elements/Img'
import { CartIcon } from '~/components/icons/generated/CartIcon'
import { SearchIcon } from '~/components/icons/generated/SearchIcon'
import { getRelatedItemAlt } from '~/features/shared/item/lib/getRelatedItemAlt'
import { sendGtmEvCoordinateBalloon } from '../../lib/gtmEvent'

type Props = {
  coordinateItem: Schemas.coordinate_item_resource
  coordinateId: number
  isBalloonVisible: boolean
  mainImageWidth: number
}

type ViewProps = {
  coordinateItem: {
    id: number
    image: { url_125: string }
    image_offset_point_x: number | null
    image_offset_point_y: number | null
    item: {
      id: number | null
      name: string
      brand: { name: string | null }
      display_price: string
      purchase_items: unknown[]
      registered_by: 'member' | 'zozo' | 'shop'
      item_category: {
        name: string
      } | null
      item_child_category: {
        name: string
      } | null
    }
    product: {
      key: string
      display_price: string
    } | null
  }
  isBalloonVisible: boolean
  mainImageWidth: number
  onClick: () => void
}

const calculateBalloonStyles = (
  offsetX: number,
  offsetY: number,
  mainImageWidth: number
) => {
  const offsetXPercent = offsetX * 100
  const offsetYPercent = offsetY * 100
  // 全体の3分の1より右だったら吹き出し位置が左になる
  const isLeft = offsetXPercent < (100 / 3) * 2

  const BALLOON_MARGIN = 20
  // タグの右端から画像右端までの距離
  const toRight = mainImageWidth * (1 - offsetX) + 14
  const maxWidth = `${
    isLeft
      ? Math.round(mainImageWidth - mainImageWidth * offsetX - BALLOON_MARGIN)
      : Math.round(mainImageWidth - toRight - BALLOON_MARGIN)
  }px`

  const top = `${offsetYPercent}%`
  const balloonStyle = isLeft
    ? { left: `${offsetXPercent}%`, top, maxWidth }
    : { right: `${100 - offsetXPercent}%`, top, maxWidth }

  /**
   * SP
   * ```
   *   +------------+
   * ＜| Brand Name |
   *   +------------+
   * ```
   * PC
   * ```
   *      +------------+----+
   * ◯ ＜| Brand Name | 🔍 |
   *      +------------+----+
   * ```
   */
  /** `＜` */
  const triangle = `before:absolute before:border-[8px] before:border-transparent before:-translate-y-1/2 before:top-1/2 xl:before:border-[5px] ${
    isLeft
      ? 'before:right-full before:left-auto before:border-r-white xl:before:border-r-black/80'
      : 'before:left-full before:right-auto before:border-l-white xl:before:border-l-black/80'
  }`
  /** `◯` */
  const circle = `after:absolute after:top-1/2 after:-translate-y-1/2 after:hidden after:size-[10px] after:rounded-full after:bg-white after:xl:block after:shadow-[0_0_2px_0_rgba(0,0,0,0.3)] ${
    isLeft ? 'after:left-[-18px]' : 'after:right-[-18px]'
  }`
  const balloonClassName = `${triangle} ${circle}`
  return { isLeft, balloonStyle, balloonClassName }
}

// eslint-disable-next-line complexity
export const CoordinateBalloonView: React.FC<ViewProps> = ({
  coordinateItem,
  isBalloonVisible,
  mainImageWidth,
  onClick,
}) => {
  const { isLeft, balloonStyle, balloonClassName } = calculateBalloonStyles(
    coordinateItem.image_offset_point_x ?? 0,
    coordinateItem.image_offset_point_y ?? 0,
    mainImageWidth ?? 0
  )

  // 吹き出しをクリックした時の遷移先
  const itemUrl = useMemo(() => {
    if (coordinateItem.product) {
      return `/products/${coordinateItem.product.key}/`
    }
    switch (coordinateItem.item.registered_by) {
      case 'member':
        return `/snapitem/${coordinateItem.id}/`
      default:
        return `/item/${coordinateItem.item.id}/`
    }
  }, [
    coordinateItem.id,
    coordinateItem.item.id,
    coordinateItem.item.registered_by,
    coordinateItem.product,
  ])

  return (
    <li
      className={`absolute -translate-y-1/2 transition duration-300 ease-coordinateTag before:!border-[6px] xl:before:!border-[5px] xl:hover:opacity-80 ${balloonClassName} ${
        isLeft ? 'ml-3 origin-[-6%_center]' : 'mr-3 origin-[106%_center]'
      } ${!isBalloonVisible ? 'scale-0 opacity-0' : ''}`}
      style={balloonStyle}
    >
      <a
        className={`flex overflow-hidden rounded-[6px] leading-none shadow-[0_2px_8px_0_rgba(0,0,0,0.15)] xl:rounded-[4px] ${
          isLeft ? '' : 'flex-row-reverse'
        }`}
        href={itemUrl}
        aria-label={getRelatedItemAlt({
          brandName: coordinateItem.item.brand.name,
          itemName: coordinateItem.item.name,
          categoryName: coordinateItem.item.item_child_category
            ? coordinateItem.item.item_child_category.name
            : coordinateItem.item.item_category?.name,
        })}
        onClick={onClick}
      >
        <div className="flex gap-1 overflow-hidden bg-white px-2 py-[7px] xl:bg-black/80 xl:px-[11px] xl:text-white">
          {coordinateItem.item.id && coordinateItem.product === null ? (
            <Img
              src={coordinateItem.image.url_125}
              alt=""
              width={125}
              height={150}
              className="size-[27px] shrink-0 rounded-full border border-black/5 object-cover xl:hidden"
            />
          ) : null}
          <div className="flex flex-col gap-[1px] overflow-hidden">
            <p className="truncate text-[13px] leading-[1.1] xl:text-[14px]">
              {coordinateItem.item.brand.name}
            </p>
            {coordinateItem.product?.display_price ? (
              <p className="pl-[1px] text-[11px] text-fleamarket xl:hidden">
                {coordinateItem.product.display_price}
              </p>
            ) : coordinateItem.item.display_price ? (
              <p className="pl-[1px] text-[11px] text-[#3A90CB] xl:hidden">
                {coordinateItem.item.display_price}
              </p>
            ) : null}
          </div>
        </div>
        {coordinateItem.item.purchase_items.length === 0 ? (
          <div className="hidden w-[30px] shrink-0 items-center justify-center bg-white xl:flex">
            <SearchIcon className="h-4 text-blue-500" />
          </div>
        ) : (
          <div className="hidden w-[30px] shrink-0 items-center justify-center bg-blue-500 xl:flex">
            <CartIcon className="h-[18px] text-white" />
          </div>
        )}
      </a>
    </li>
  )
}

export const CoordinateBalloon: React.FC<Props> = ({
  coordinateItem,
  coordinateId,
  isBalloonVisible,
  mainImageWidth,
}) => {
  const onClick = useCallback(() => {
    const hasCoupon = !!coordinateItem.item.purchase_items?.[0]?.coupon
    sendGtmEvCoordinateBalloon(
      coordinateItem.item.id
        ? `snapID${coordinateId}_itemID${coordinateItem.item.id}_coupon${hasCoupon ? 1 : 0}`
        : `snapID${coordinateId}_snapitemID${coordinateItem.id}`
    )
  }, [
    coordinateId,
    coordinateItem.id,
    coordinateItem.item.id,
    coordinateItem.item.purchase_items,
  ])

  return (
    <CoordinateBalloonView
      coordinateItem={coordinateItem}
      isBalloonVisible={isBalloonVisible}
      mainImageWidth={mainImageWidth}
      onClick={onClick}
    />
  )
}
